import {isUndefined} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {UnreachableError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {UserTheme, useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  SearchLocalFilterIconType,
  SearchLocalFilterTypesEnum,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersFiltersIconHelpers';
import {TeamAvatar} from '../../../common/avatars/teamAvatar';
import {TeammateAvatar} from '../../../common/avatars/teammateAvatar';
import {ColorDot} from '../../../common/colorDot';
import {Emoji} from '../../../common/emoji/emoji';
import {FilterButton} from '../../../common/filters/common/filterButton';
import {maybeReplaceSearchLocalFilterIcon} from './searchHintsHelpers';

/*
 * Props.
 */

export interface SearchHintItemPillProps {
  title: ReactNode;
  icon: SearchLocalFilterIconType;
  onClick?: Handler;
  isSelected: boolean;
  isDisabled: boolean;
  isHovered?: boolean;
  interactionId?: string;
}

const FilterContentWrapper = styled.div`
  display: grid;
  grid-template-columns: max-content max-content;
  grid-gap: 8px;
  align-items: center;
`;

/*
 * Component.
 */

export const SearchHintItemPill: FC<SearchHintItemPillProps> = (props) => {
  const {title, icon, onClick, isHovered, isSelected, interactionId} = props;
  // We're only passing `isHovered` in case we're using the pill as a dropdown button.
  const isDropdown = !isUndefined(isHovered);
  const theme = useUserTheme();

  return (
    <FilterButton
      isActive={isSelected}
      onClick={onClick}
      hasForcedHover={isHovered}
      hasChevron={isDropdown}
      interactionId={interactionId}
    >
      <FilterContentWrapper>
        {renderIcon(icon, isSelected, isHovered, theme)}
        {title}
      </FilterContentWrapper>
    </FilterButton>
  );
};

/*
 * Helpers.
 */

function renderIcon(
  icon: SearchLocalFilterIconType,
  isSelected: boolean,
  isHovered: boolean | undefined,
  theme: UserTheme,
) {
  switch (icon.type) {
    case SearchLocalFilterTypesEnum.EMOJI:
      return <Emoji {...icon.emoji} size={16} />;

    case SearchLocalFilterTypesEnum.ICON:
      return (
        <Icon
          name={maybeReplaceSearchLocalFilterIcon(icon, isSelected)}
          color={getIconColor(isSelected, theme)}
        />
      );

    case SearchLocalFilterTypesEnum.DOT: {
      return (
        <ColorDot
          size={8}
          color={icon.color ?? (isHovered ? theme.greys.shade50 : theme.greys.shade40)}
          hoverColor={icon.color || theme.greys.shade50}
        />
      );
    }

    case SearchLocalFilterTypesEnum.TEAM:
      return <TeamAvatar team={icon.team} size={VisualSizesEnum.SMALL} />;

    case SearchLocalFilterTypesEnum.TEAMMATE:
      return <TeammateAvatar teammate={icon.teammate} size={VisualSizesEnum.SMALL} />;

    default:
      throw new UnreachableError(icon);
  }
}

const getIconColor = (isSelected: boolean, theme: UserTheme) =>
  isSelected ? theme.palette[PaletteColorsEnum.BLUE].shade40 : theme.greys.shade80;
